import { ProgramType } from 'types';

import { api } from './api';

const getArrayTiltValues = (arrays: Array<{ tilt?: number; tiltUnit?: string }> | undefined) =>
  (arrays ?? []).map(({ tilt, tiltUnit }) => ({
    tilt: tilt ?? null,
    tiltUnit: tiltUnit ?? null,
  }));

export const systemDesignApi = api.injectEndpoints({
  endpoints: (build) => ({
    createSystemDesign: build.mutation<
      any,
      { systemDesign: Record<string, any>; accountId: string; programType: ProgramType }
    >({
      query: ({ systemDesign, accountId, programType }) => {
        let url = `accounts/${accountId}/system-design`;
        switch (programType) {
          case ProgramType.hvac:
            url = `${url}/hvac`;
            break;
          case ProgramType.waterHeater:
            url = `${url}/water-heater`;
            break;
          case ProgramType.generator:
            url = `${url}/generator`;
            break;
          case ProgramType.geothermal:
            url = `${url}/geothermal`;
            break;
          case ProgramType.energyBackup:
            url = `${url}/energy-backup`;
            break;
          default:
            break;
        }
        return {
          url,
          method: 'POST',
          body: systemDesign,
        };
      },
      invalidatesTags: (_result, _error, args) => [
        // getCurrentDesign provides { type: 'SystemDesign', id: accountId }; invalidation must use the same id field.
        { type: 'SystemDesign', id: args.accountId },
        { type: 'Accounts', id: args.accountId },
        { type: 'Pricing', id: args.accountId },
        { type: 'AccountMilestones', id: args.accountId },
        { type: 'Tasks', id: 'LIST' },
        { type: 'Queues', accountId: args.accountId },
        { type: 'Contracts', id: 'LIST' },
      ],
      onQueryStarted: async ({ accountId, programType, systemDesign }, queryLifecycleApi) => {
        const { dispatch, queryFulfilled } = queryLifecycleApi;
        const cached = systemDesignApi.endpoints.getCurrentDesign.select({ id: accountId, programType })(
          queryLifecycleApi.getState(),
        );
        const previousArrayTilt = JSON.stringify(getArrayTiltValues(cached.data?.arrays));
        const updatedArrayTilt = JSON.stringify(getArrayTiltValues(systemDesign.arrays));
        // Only invalidate checklists when the array tilt data actually changed
        const arraysChanged = previousArrayTilt !== updatedArrayTilt;

        try {
          await queryFulfilled;
          if (arraysChanged) {
            dispatch(api.util.invalidateTags([{ type: 'Checklists' }]));
          }
        } catch {
          // mutation failed — no invalidation needed
        }
      },
    }),

    getCurrentDesign: build.query<any, { id: string; programType: ProgramType }>({
      query: ({ id, programType }) => {
        let url = `accounts/${id}/system-design`;
        switch (programType) {
          case ProgramType.hvac:
            url = `${url}/hvac`;
            break;
          case ProgramType.waterHeater:
            url = `${url}/water-heater`;
            break;
          case ProgramType.generator:
            url = `${url}/generator`;
            break;
          case ProgramType.geothermal:
            url = `${url}/geothermal`;
            break;
          case ProgramType.energyBackup:
            url = `${url}/energy-backup`;
            break;
          default:
            break;
        }
        return {
          url: `${url}/current`,
        };
      },
      // The account id is the stable cache key for the current design endpoint.
      providesTags: (_result, _error, args) => [{ type: 'SystemDesign', id: args.id }],
    }),
  }),
});

export const { useCreateSystemDesignMutation, useGetCurrentDesignQuery, useLazyGetCurrentDesignQuery } =
  systemDesignApi;
